import { useState } from 'react';
import { useUsuals } from '@/hooks/useUsuals';
import { ReorderValidationModal } from '@/components/ReorderValidationModal';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Store } from 'lucide-react';

interface ReorderData {
  orderId: string;
  restaurantId: string;
  eatzyClientId: string;
  serviceType: 'takeout' | 'delivery';
  deliveryLat?: number;
  deliveryLng?: number;
  deliveryAddressId?: number;
}

export default function UsualsWidget() {
  const { usuals, isLoading } = useUsuals(6);
  const [reorderData, setReorderData] = useState<ReorderData | null>(null);

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-1 px-4" style={{ scrollbarWidth: 'none' }}>
        {[1, 2, 3].map(i => <Skeleton key={i} className="flex-shrink-0 w-[150px] h-[90px] rounded-lg" />)}
      </div>
    );
  }

  if (usuals.length === 0) {
    return (
      <div className="px-4 py-3 text-center text-sm text-muted-foreground">
        Order from a restaurant to see your usuals here.
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-3 overflow-x-auto pb-1 px-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {usuals.map((usual) => (
          <Card
            key={usual.restaurantId}
            className="flex-shrink-0 w-[150px] hover-elevate active-elevate-2 cursor-pointer"
            data-testid={`usual-${usual.restaurantId}`}
            onClick={() =>
              setReorderData({
                orderId: String(usual.lastOrder.id),
                restaurantId: usual.restaurantId,
                eatzyClientId: usual.eatzyClientId,
                serviceType: usual.lastOrder.type,
                deliveryLat: usual.lastOrder.deliveryLat,
                deliveryLng: usual.lastOrder.deliveryLng,
                deliveryAddressId: usual.lastOrder.deliveryAddressId,
              })
            }
          >
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                {usual.restaurantLogo ? (
                  <Avatar className="w-7 h-7 flex-shrink-0">
                    <AvatarImage src={usual.restaurantLogo} alt={usual.restaurantName} />
                    <AvatarFallback className="text-xs">{usual.restaurantName.charAt(0)}</AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Store className="w-3.5 h-3.5 text-primary" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-xs truncate leading-tight">{usual.restaurantName}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-1">
                <span className="text-xs text-muted-foreground">{usual.count}× ordered</span>
                <Badge variant="secondary" className="text-xs px-1.5">Order</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {reorderData && (
        <ReorderValidationModal
          isOpen={!!reorderData}
          onClose={() => setReorderData(null)}
          orderId={reorderData.orderId}
          restaurantId={reorderData.restaurantId}
          eatzyClientId={reorderData.eatzyClientId}
          serviceType={reorderData.serviceType}
          deliveryLat={reorderData.deliveryLat}
          deliveryLng={reorderData.deliveryLng}
          deliveryAddressId={reorderData.deliveryAddressId}
          onSuccess={() => setReorderData(null)}
        />
      )}
    </>
  );
}
